import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

function hasChildOfType(
  children: React.ReactNode,
  displayName: string | undefined,
): boolean {
  let found = false;
  React.Children.forEach(children, (child) => {
    if (found || !React.isValidElement(child)) return;
    const type = child.type as { displayName?: string } | string;
    if (typeof type !== "string" && type?.displayName === displayName) {
      found = true;
      return;
    }
    const nested = (child.props as { children?: React.ReactNode })?.children;
    if (nested && hasChildOfType(nested, displayName)) found = true;
  });
  return found;
}

/**
 * Screen-reader-only title for a dialog that has none. Radix gives every Title in
 * a dialog the same id, so a second element with this id means the dialog
 * rendered a real DialogTitle (e.g. inside a child component) and the fallback
 * can go. The check runs here, not in DialogContent, because the portal puts the
 * content in the DOM a render after DialogContent itself commits.
 */
function FallbackDialogTitle({
  children,
  onRealTitle,
}: {
  children: React.ReactNode;
  onRealTitle?: () => void;
}) {
  const ref = React.useRef<HTMLHeadingElement>(null);
  React.useLayoutEffect(() => {
    const id = ref.current?.id;
    if (onRealTitle && id && document.querySelectorAll(`[id="${CSS.escape(id)}"]`).length > 1) {
      onRealTitle();
    }
  });
  return (
    <DialogPrimitive.Title ref={ref} className="sr-only">
      {children}
    </DialogPrimitive.Title>
  );
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean;
    title?: string;
    description?: string;
  }
>(({ className, children, hideCloseButton = false, description, title, ...props }, ref) => {
  const shouldInjectFallbackDescription =
    description !== undefined ||
    !hasChildOfType(children, DialogPrimitive.Description.displayName);

  // Radix logs "DialogContent requires a DialogTitle" (in production too) when an
  // open dialog has no title, and screen readers then announce it unlabelled
  // (BREEZEAI-1361 BUG-05). The static check misses a DialogTitle rendered inside
  // a child component; FallbackDialogTitle catches that case once it's in the DOM.
  const [hasRenderedTitle, setHasRenderedTitle] = React.useState(false);
  const shouldInjectFallbackTitle =
    title !== undefined ||
    (!hasRenderedTitle &&
      !hasChildOfType(children, DialogPrimitive.Title.displayName));
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-3 border border-surface-border bg-surface-elevated p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        {...props}
      >
        {shouldInjectFallbackTitle && (
          <FallbackDialogTitle
            onRealTitle={title === undefined ? () => setHasRenderedTitle(true) : undefined}
          >
            {title ?? "Dialog"}
          </FallbackDialogTitle>
        )}
        {shouldInjectFallbackDescription && (
          <DialogPrimitive.Description className="sr-only">
            {description ?? "Dialog content"}
          </DialogPrimitive.Description>
        )}
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close
            aria-label="Close"
            className="absolute right-4 top-4 rounded-sm opacity-70 text-text-secondary
   ring-offset-surface-elevated transition-opacity hover:opacity-100 hover:text-text-primary focus:outline-none focus:ring-2
    focus:ring-primary focus:ring-offset-2 disabled:pointer-events-none
   data-[state=open]:bg-surface-hover"
          >
            <X aria-hidden="true" className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight text-text-primary",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
